import React, { useState, useEffect, useRef } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import PropTypes from 'prop-types';
import useImage from 'use-image';
import ResetButtonIcon from './ResetButtonIcon';
import DrawLine from './DrawLine';
import CircleWithText from './CircleWithText';
import { PATROL_MAP_LINE } from '../../../../utils/constants/patrol';
import { cloneDeep } from 'lodash';
import TaskIcon from './TaskIcon';

const ImageWithRegions = ({
  markers,
  markerDraggable = true,
  markerDragStart,
  markerDragEnd,
  onImageClick,
  markerClick,
  customMapControl,
  imageUrl,
  passPropsAndHandlers,
  hidePolylines = false,
}) => {
  const deleteButtonRef = useRef();
  const textRef = useRef([]);
  const CIRCLE_RADIUS = 10;
  const [state, setState] = useState({
    points: [],
    returnToStart: true,
  });

  const [image] = useImage(imageUrl);

  useEffect(() => {
    if (
      markers.length > 2 &&
      markers[0].lat === markers[markers.length - 1].lat &&
      markers[0].lng === markers[markers.length - 1].lng
    ) {
      // if first and last points are similar that means return to start was true
      const clonedPoints = cloneDeep(markers);
      clonedPoints.pop();
      setState(prev => ({
        ...prev,
        points: clonedPoints,
        returnToStart: true,
      }));
    } else {
      setState(prev => ({ ...prev, points: markers, returnToStart: false }));
    }

    return () => {
      setState(prev => ({ ...prev, points: [] }));
    };
  }, [markers, hidePolylines]);

  const getMousePos = stage => {
    return [stage.getPointerPosition().x, stage.getPointerPosition().y];
  };

  const diff = (num1, num2) => {
    if (num1 > num2) {
      return num1 - num2;
    } else {
      return num2 - num1;
    }
  };

  // this method adds circle
  const handleClick = event => {
    const { points } = state;

    const stage = event.target.getStage();
    if (!stage) {
      return;
    }
    const mousePos = getMousePos(stage);
    const dist = [];

    // check if the mouse is inside the circle, don't add the point to the canvas if it lies outside the circle
    // loop through all the stored points and compare it with the mouse poition using pythagoras
    points.forEach(point => {
      const deltaX = diff(mousePos[0], point['lat']);
      const deltaY = diff(mousePos[1], point['lng']);
      // calculate the distance between two points
      const distance = Math.sqrt(Math.pow(deltaX, 2) + Math.pow(deltaY, 2));
      // add the distance found to the dist object
      dist.push(distance);
    });

    // if any point is less than 10px it should not add the point
    if (dist.filter(f => f < CIRCLE_RADIUS).length > 0) {
      return;
    }

    onImageClick({
      x: mousePos[0],
      y: mousePos[1],
    });
  };

  const handleDragStartPoint = event => {
    if (typeof markerDragStart === 'function') {
      const pos = [event.target.attrs.x, event.target.attrs.y];
      markerDragStart({ x: pos[0], y: pos[1] });
    }
  };

  const handleDragEndPoint = event => {
    if (typeof markerDragEnd === 'function') {
      const pos = [event.target.attrs.x, event.target.attrs.y];
      markerDragEnd({ x: pos[0], y: pos[1] });
    }
  };

  const flattenedPoints = () => {
    if (state.points.length < 1) {
      return [];
    }
    return state.points.map(point => [point.lat, point.lng]).flatMap(f => f);
  };

  const firstAndLastPoint = () => {
    if (state.points.length < 3) {
      return [];
    }
    const fp = flattenedPoints();
    return [fp[0], fp[1], fp[fp.length - 2], fp[fp.length - 1]]; // first and last point
  };

  const handleCircleClick = (x, y) => {
    if (typeof markerClick === 'function') {
      markerClick({ x: x, y: y });
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          overflowX: 'scroll',
          overflowY: 'scroll',
        }}
      >
        <Stage
          width={image && image.width}
          height={image && image.height}
          onMouseDown={passPropsAndHandlers ? handleClick : null}
        >
          <Layer>
            <KonvaImage image={image} />
          </Layer>

          <Layer>
            {!hidePolylines &&
              (state.returnToStart ? (
                <DrawLine
                  points={firstAndLastPoint()}
                  stroke={PATROL_MAP_LINE.strokeColor}
                  strokeWidth={PATROL_MAP_LINE.strokeWeight}
                />
              ) : (
                <DrawLine
                  dotted={true}
                  points={firstAndLastPoint()}
                  dash={[
                    PATROL_MAP_LINE.strokeWeight,
                    PATROL_MAP_LINE.strokeWeight,
                  ]}
                  stroke={PATROL_MAP_LINE.strokeColor}
                />
              ))}
          </Layer>
          <Layer>
            {!hidePolylines && (
              <DrawLine
                points={flattenedPoints()}
                stroke={PATROL_MAP_LINE.strokeColor}
                strokeWidth={PATROL_MAP_LINE.strokeWeight}
              />
            )}

            {state.points.length > 0 &&
              state.points.map((point, index) => {
                const x = point.lat;
                const y = point.lng;
                const val = point.text;
                const seqNum = point.seqNumber;
                const icon = state.points[index]?.icon;
                const startPointAttr =
                  index === 0
                    ? {
                        hitStrokeWidth: 12,
                      }
                    : passPropsAndHandlers && {
                        onclick: () => handleCircleClick(x, y), // this method is for all points except 1
                      };

                if (icon) {
                  return <TaskIcon x={x} y={y} icon={icon} key={index} />;
                }
                return (
                  <CircleWithText
                    x={x}
                    y={y}
                    seqNum={seqNum}
                    key={index}
                    index={index}
                    handleDragStartPoint={
                      passPropsAndHandlers ? handleDragStartPoint : null
                    }
                    handleDragEndPoint={
                      passPropsAndHandlers ? handleDragEndPoint : null
                    }
                    markerDraggable={
                      passPropsAndHandlers ? markerDraggable : false
                    }
                    startPointAttr={startPointAttr}
                    radius={CIRCLE_RADIUS}
                    textValue={val}
                    textRef={textRef} // needs an array of refs since circles with texts can be more than 1
                  />
                );
              })}
          </Layer>
          <Layer>
            {state.points.length > 0 && passPropsAndHandlers && (
              //TO-DO find absolute positioning for reset button
              <ResetButtonIcon
                x={25}
                y={25}
                deletePoints={customMapControl}
                deleteButtonref={deleteButtonRef}
              />
            )}
          </Layer>
        </Stage>
      </div>
    </>
  );
};

ImageWithRegions.propTypes = {
  label: PropTypes.string,
  markers: PropTypes.array,
  markerDraggable: PropTypes.bool,
  markerDragStart: PropTypes.func,
  markerDragEnd: PropTypes.func,
  onImageClick: PropTypes.func,
  markerClick: PropTypes.func,
  customMapControl: PropTypes.func,
  value: PropTypes.array,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  info: PropTypes.string,
  maxPoints: PropTypes.number,
  formProps: PropTypes.object,
  locationEvidence: PropTypes.object,
  displayEvidence: PropTypes.bool,
  hidePolylines: PropTypes.bool,
  imageUrl: PropTypes.string,
  passPropsAndHandlers: PropTypes.func,
};

ImageWithRegions.defaultProps = {
  passPropsAndHandlers: true, // if you have to statically use this like a display
  //only as a component as used in detail panel for patrol, pass this prop as false
};
export default React.memo(ImageWithRegions, (prev, curr) => {
  return (
    prev.markers.length === curr.markers.length &&
    prev.imageUrl === curr.imageUrl &&
    prev.markers.text !== curr.markers.text
  );
});
